import { apiClient } from './apiClient';

export interface CollaborationRequest {
  id: string;
  fromUserId?: string; // Optional - null for non-logged-in users
  toUserId: string;
  message: string;
  additionalMessage?: string;
  status: 'sent' | 'viewed' | 'responded';
  createdAt: string;
  updatedAt: string;
  fromUserFullName: string; // Always present - either from user profile or provided name
  toUserFullName: string;
  fromUserEmail: string; // Always present - either from user profile or provided email
  fromUserWhatsappNumber: string; // Always present - either from user profile or provided number
  platform: 'whatsapp';
  deviceType: 'mobile' | 'desktop';
}

export interface CreateCollaborationRequestData {
  toUserId: string;
  message: string;
  additionalMessage?: string;
  platform: 'whatsapp';
  deviceType: 'mobile' | 'desktop';
  fromUserFullName?: string; // Required for non-logged-in users
  fromUserEmail?: string; // Optional for non-logged-in users
  fromUserWhatsappNumber?: string; // Optional for non-logged-in users
}

export interface CollaborationStats {
  totalRequestsSent: number;
  totalRequestsReceived: number;
  responseRate: number;
  recentRequests: CollaborationRequest[];
}

class CollaborationService {
  // Create a new collaboration request
  async createCollaborationRequest(data: CreateCollaborationRequestData): Promise<{
    success: boolean;
    data?: CollaborationRequest;
    error?: string;
  }> {
    try {
      const response = await apiClient.post<CollaborationRequest>('/collaboration/requests', data);
      
      if (!response.success) {
        return {
          success: false,
          error: response.error.message || 'Failed to create collaboration request',
        };
      }
      
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Failed to create collaboration request:', error);
      return {
        success: false,
        error: 'Failed to create collaboration request',
      };
    }
  }

  // Get collaboration requests sent by user
  async getSentRequests(userId?: string, limit = 20): Promise<{
    success: boolean;
    data?: CollaborationRequest[];
    error?: string;
  }> {
    try {
      const params = new URLSearchParams();
      if (userId) params.append('userId', userId);
      params.append('limit', limit.toString());
      
      const response = await apiClient.get<CollaborationRequest[]>(`/collaboration/requests/sent?${params}`);
      
      if (!response.success) {
        return {
          success: false,
          error: response.error.message || 'Failed to fetch sent requests',
        };
      }
      
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Failed to fetch sent requests:', error);
      return {
        success: false,
        error: 'Failed to fetch sent requests',
      };
    }
  }

  // Get collaboration requests received by user
  async getReceivedRequests(userId?: string, limit = 20): Promise<{
    success: boolean;
    data?: CollaborationRequest[];
    error?: string;
  }> {
    try {
      const params = new URLSearchParams();
      if (userId) params.append('userId', userId);
      params.append('limit', limit.toString());
      
      const response = await apiClient.get<CollaborationRequest[]>(`/collaboration/requests/received?${params}`);
      
      if (!response.success) {
        return {
          success: false,
          error: response.error.message || 'Failed to fetch received requests',
        };
      }
      
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Failed to fetch received requests:', error);
      return {
        success: false,
        error: 'Failed to fetch received requests',
      };
    }
  }

  // Get collaboration statistics for a user
  async getCollaborationStats(userId?: string): Promise<{
    success: boolean;
    data?: CollaborationStats;
    error?: string;
  }> {
    try {
      const params = userId ? `?userId=${userId}` : '';
      const response = await apiClient.get<CollaborationStats>(`/collaboration/stats${params}`);
      
      if (!response.success) {
        return {
          success: false,
          error: response.error.message || 'Failed to fetch collaboration stats',
        };
      }
      
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Failed to fetch collaboration stats:', error);
      return {
        success: false,
        error: 'Failed to fetch collaboration stats',
      };
    }
  }

  // Update collaboration request status (when viewed/responded)
  async updateRequestStatus(requestId: string, status: 'viewed' | 'responded'): Promise<{
    success: boolean;
    data?: CollaborationRequest;
    error?: string;
  }> {
    try {
      const response = await apiClient.patch<CollaborationRequest>(`/collaboration/requests/${requestId}/status`, { status });
      
      if (!response.success) {
        return {
          success: false,
          error: response.error.message || 'Failed to update request status',
        };
      }
      
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Failed to update request status:', error);
      return {
        success: false,
        error: 'Failed to update request status',
      };
    }
  }
}

export const collaborationService = new CollaborationService();
export default collaborationService;
